import React, { useState } from 'react';
import { Lock, Eye, EyeOff } from 'lucide-react';

/**
 * PasswordInput — password field with a right-side eye toggle to reveal/hide the value.
 *
 * Props:
 *   value, onChange (event or value based on `onChangeValue` flag)
 *   testId          → data-testid for the input; toggle button gets `${testId}-toggle`
 *   placeholder, required, minLength, autoComplete
 *   className       → overrides the default input container class
 *   icon            → optional Lucide icon component to show on the left (defaults to Lock)
 *   onChangeValue   → if true, calls onChange(value); else calls onChange(event)
 */
export default function PasswordInput({
  value,
  onChange,
  testId = 'password-input',
  placeholder = 'Password',
  required = false,
  minLength,
  autoComplete = 'current-password',
  className = 'w-full bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg pl-10 pr-11 py-2.5 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600 focus:ring-1 focus:ring-slate-300 dark:focus:ring-zinc-600 transition-colors text-sm',
  icon: Icon = Lock,
  onChangeValue = false,
}) {
  const [show, setShow] = useState(false);

  const handleChange = (e) => {
    if (onChangeValue) onChange(e.target.value);
    else onChange(e);
  };

  return (
    <div className="relative">
      {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-zinc-500 pointer-events-none" />}
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        minLength={minLength}
        autoComplete={autoComplete}
        data-testid={testId}
        className={className}
      />
      <button
        type="button"
        onClick={() => setShow(s => !s)}
        tabIndex={-1}
        aria-label={show ? 'Hide password' : 'Show password'}
        title={show ? 'Hide password' : 'Show password'}
        data-testid={`${testId}-toggle`}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-zinc-500 hover:text-slate-700 dark:hover:text-zinc-200 transition-colors"
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}
